import { createRouter, createWebHistory } from 'vue-router'
import { checkoutRoutes } from '../modules/checkout/routes';
import { settingsRoute } from '../modules/settings/routes'
import { schedulingRoutes, calendarRoutes } from '../modules/scheduling/routes'
import { isLoading, appVm, versionCheck, module, systemUserLoaded, permissionMap, systemUser, initUnreadInterval, clearUnreadInterval } from '@/main-store'
import { clientRoutes } from '@/modules/clients/routes';
import { useModalStore } from '@ui/modal/modal-store';
import { reportingRoutes } from '@/modules/reports/routes';
import { accountingRoutes } from '@/modules/accounting/routes';
import Enums from '@/Enums';
import Permissions from '@utils/Permissions';
import { medicalRoutes } from '@/modules/medical/routes';
import { adminRoutes } from '@/modules/admin/routes';
import { helpRoutes } from '@/modules/help/routes';
import type { Ref } from 'vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'App',
      redirect: '/dashboard'
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/modules/dashboard/DashboardView.vue')
    },
    {
      path: '/billing',
      name: 'Organization & Billing',
      component: () => import('@/modules/billing/BillingOverview.vue'),
      meta: { defaultLayout: true }
    },
    {
      path: '/e-lobby',
      name: 'E-Lobby',
      component: () => import('@/modules/e-lobby/ELobby.vue'),
      meta: { defaultLayout: false, layoutMode: 'fullscreen' }
    },
    {
      path: '/access-denied',
      name: 'Access Denied',
      component: () => import('@/modules/AccessDenied.vue'),
    },
    {
      // Catches all unmatched paths
      path: '/:pathMatch(.*)*',
      name: 'Not Found',
      component: () => import('@/modules/NotFound.vue'),
      meta: { defaultLayout: true }
    },
    {
      // Catches legacy Angular hash routes like /app#/dashboard → /dashboard
      path: '/app',
      name: 'Rewrite',
      beforeEnter: () => {
        const hash = window.location.hash
        const path = hash ? hash.replace('#', '') : '/'
        window.location.replace(path)
        return false
      },
      component: () => import('@/modules/NotFound.vue')
    },
    checkoutRoutes,
    clientRoutes,
    schedulingRoutes,
    calendarRoutes,
    reportingRoutes,
    accountingRoutes,
    settingsRoute,
    medicalRoutes,
    helpRoutes,
    adminRoutes
  ],
})

router.beforeEach(async (to, from, next) => {
  await versionCheck();
  isLoading.value = true;
  next();
});

// Hide loader after navigation completes
router.afterEach((to) => {
  isLoading.value = false

  appVm.title = to.name?.toString() || ''
  appVm.layoutMode = to.meta?.layoutMode || 'sidebar'
  appVm.defaultLayout = to.meta?.defaultLayout || false
  module.value = to.path?.split('/')[1]

  if (window.innerWidth < 1024) {
    appVm.toggleSideMenu = false;
  }

  systemUserLoaded.promise.then(function () {
    toggleChatInterval(module);

    if (systemUser.clinic.subscriptionStatus == Enums.SubscriptionStatus.Suspended &&
      systemUser.permissions.includes(Permissions.Billing.AccessBilling) &&
      !systemUser.isSuperAdmin) {
      router.push({ path: '/billing' });
      return;
    }

    if (module.value != 'access-denied' && !checkPermission(systemUser.permissions, module.value)) {
      router.push({ path: '/access-denied' });
    }
  })

  useModalStore().dismissAll();
});

function checkPermission(permissions: string[], module: string) {
  if (['e-lobby', 'help', 'medical', 'dashboard', 'divvy', 'chat'].includes(module)) {
    return true;
  }
  return permissions.includes(permissionMap[module]);
}

function toggleChatInterval(module: Ref<string>) {
  if (!systemUser.clinic.isChatActive) {
    return;
  }

  if (!['e-lobby', 'patient-flow', 'am-tasks', 'admin'].includes(module.value)) {
    initUnreadInterval();
    return;
  }

  clearUnreadInterval();
}

export default router
